/**
 * Calendar section embedded on the patient dashboard.
 *
 * Extracted from the former standalone CalendarPage so the 30-day adherence
 * grid, day-detail panel, PDF download, and per-med adherence stats all live
 * on the dashboard as a single inline section. The /patient/calendar route
 * now redirects to /patient (see App.tsx).
 *
 * Quick edits happen directly in the grid: a med square toggles taken/not-taken
 * on click, and a pain square opens a compact 0–10 picker anchored right under
 * the cell so the cursor barely moves. A small note icon per day opens the
 * bottom card, which is now purely for typing a free-text note for that day.
 *
 * Keeps the existing `cal-*` CSS classes from patient.css so styling carries
 * over; the popover / note-row classes are additive.
 */

import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import {
  useCalendar,
  type CalendarResponse,
  type CalendarPainDay,
  type CalendarTrackingDay,
} from '@pelvia/shared/hooks/patient/useCalendar'
import { useCyclePredictor } from '@pelvia/shared/hooks/patient/useCyclePredictor'
import { painColor, painTextColor } from '@pelvia/shared/lib/patient/painColors'
import { usePelviaCare } from '@/hooks/usePelviaCare'

// Logged-bleeding flow colors (strong) — these win when the user actually
// recorded flow on a day.
const CYCLE_COLORS: Record<string, string> = {
  period: '#E8B0BC',
  heavy: '#D4607A',
  moderate: '#E8B0BC',
  light: '#F4CAD2',
  spotting: '#F0C8A8',
  clot_passing: '#D4607A',
  ovulation: '#A0D0B4',
}

// Subtle predicted-phase tints used when the user hasn't logged bleeding that
// day, so the calendar reflects the predicted cycle phase across the month.
const PHASE_TINT: Record<string, string> = {
  period: 'rgba(201, 64, 64, 0.18)',
  follicular: 'rgba(232, 164, 74, 0.16)',
  ovulation: 'rgba(74, 140, 92, 0.18)',
  luteal: 'rgba(107, 94, 168, 0.16)',
}

function dateKey(d: Date): string {
  return d.toLocaleDateString('en-CA')
}

function formatDay(key: string): string {
  return new Date(key + 'T12:00:00').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

// Anchor the floating pain picker under (or above, near the bottom of the
// viewport) the clicked cell, clamped to the viewport horizontally.
const PICKER_W = 300
const PICKER_H = 46
function pickerStyle(rect: DOMRect): React.CSSProperties {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 360
  const vh = typeof window !== 'undefined' ? window.innerHeight : 640
  const left = Math.min(Math.max(8, rect.left + rect.width / 2 - PICKER_W / 2), vw - PICKER_W - 8)
  const below = rect.bottom + 6
  const top = below + PICKER_H > vh ? rect.top - PICKER_H - 6 : below
  return { position: 'fixed', left, top, width: PICKER_W }
}

// Height-matched loading state. The calendar always renders once data arrives,
// so reserving its real grid height here (vs a one-line "Loading…") keeps the
// rest of the dashboard feed from shifting down on arrival (CLS). Mirrors the
// loaded structure: header + day-number row, cycle strip, a couple of metric
// rows, pain row, and the legend. Med-row count is approximated (2) since the
// real count isn't known until the data loads.
function CalendarSkeleton({
  days,
  isMobile,
  dayKeys,
}: {
  days: number
  isMobile: boolean
  dayKeys: string[]
}) {
  const colTemplate = isMobile
    ? `80px repeat(${days}, 1fr)`
    : `110px repeat(${days}, 1fr)`
  const metricRows = 3 // ~2 meds + pain row, the common case
  return (
    <section className="card" aria-busy="true" aria-label="Loading your calendar">
      <div className="cal-header-row">
        <span className="skeleton-block" style={{ width: 220, height: 18 }} />
        <span className="skeleton-block" style={{ width: 150, height: 28, borderRadius: 999 }} />
      </div>
      <div className="cal-grid-wrap">
        <div className="cal-row" style={{ gridTemplateColumns: colTemplate }}>
          <div />
          {dayKeys.map((k) => (
            <div key={k} className="cal-hcell cal-skeleton-hcell" />
          ))}
        </div>
        <div className="cal-row" style={{ gridTemplateColumns: colTemplate }}>
          <div className="cal-label">
            <span className="skeleton-block" style={{ width: 36, height: 10 }} />
          </div>
          {dayKeys.map((k) => (
            <div key={k} className="cal-cycle-cell skeleton-block" />
          ))}
        </div>
        {Array.from({ length: metricRows }).map((_, r) => (
          <div key={r} className="cal-row" style={{ gridTemplateColumns: colTemplate }}>
            <div className="cal-label">
              <span className="skeleton-block" style={{ width: 52, height: 10 }} />
            </div>
            {dayKeys.map((k) => (
              <div key={k} className="cal-cell cal-skeleton-cell skeleton-block" />
            ))}
          </div>
        ))}
      </div>
      <div className="cal-legend">
        {Array.from({ length: 4 }).map((_, i) => (
          <span key={i} className="skeleton-block" style={{ width: 64, height: 12 }} />
        ))}
      </div>
    </section>
  )
}

export default function CalendarSection() {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
  const days = isMobile ? 14 : 30
  const { data, isLoading, error } = useCalendar(days)
  const { data: cycleData } = useCyclePredictor()
  const phaseCalendar = cycleData?.phase_calendar
  const queryClient = useQueryClient()

  const today = dateKey(new Date())
  // Which day's note card is open, and which pain cell (with its anchor rect)
  // has the inline picker open. Only one of each at a time.
  const [noteCardFor, setNoteCardFor] = useState<string | null>(null)
  const [painPicker, setPainPicker] = useState<{ k: string; rect: DOMRect } | null>(null)
  const pc = usePelviaCare()

  const dayKeys = useMemo(() => {
    const arr: string[] = []
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date()
      d.setDate(d.getDate() - i)
      arr.push(dateKey(d))
    }
    return arr
  }, [days])

  const refreshCalendar = () => {
    queryClient.invalidateQueries({ queryKey: ['patient', 'calendar'] })
  }

  // Optimistically flip a med's adherence for a day, then persist + resync.
  const toggleMed = (medId: number, k: string, taken: boolean) => {
    queryClient.setQueryData<CalendarResponse>(['patient', 'calendar', days], (prev) => {
      if (!prev) return prev
      const adherence_map = { ...prev.adherence_map }
      adherence_map[k] = { ...(adherence_map[k] || {}), [medId]: !taken }
      return { ...prev, adherence_map }
    })
    api
      .post('/api/patient/medications/adherence', {
        medication_id: medId,
        taken: !taken,
        log_date: k,
      })
      .catch(() => {})
      .finally(refreshCalendar)
  }

  // Log/overwrite a pain score for a day from the inline picker.
  const logPain = (k: string, score: number) => {
    setPainPicker(null)
    queryClient.setQueryData<CalendarResponse>(['patient', 'calendar', days], (prev) => {
      if (!prev) return prev
      const pain_map = { ...prev.pain_map }
      pain_map[k] = { ...(pain_map[k] || {}), pain_score: score, zero_filled: false }
      return { ...prev, pain_map }
    })
    api
      .post('/api/patient/pain-log', { pain_score: score, log_date: k })
      .catch(() => {})
      .finally(refreshCalendar)
  }

  if (isLoading) {
    return <CalendarSkeleton days={days} isMobile={isMobile} dayKeys={dayKeys} />
  }

  if (error || !data) {
    return (
      <section className="card">
        <p style={{ color: 'var(--text-muted)' }}>
          Couldn&apos;t load calendar data. Please try again in a moment.
        </p>
      </section>
    )
  }

  const meds = data.medications || []
  const painMap = data.pain_map || {}
  const adhMap = data.adherence_map || {}
  const trackMap = data.tracking_map || {}
  const adhStats = data.adherence_stats || []
  const loggedDays = Object.values(painMap).filter((p) => !p.zero_filled).length

  // Which day is visually "active" (picker or note card open).
  const activeDay = painPicker?.k ?? noteCardFor ?? null

  const colTemplate = isMobile
    ? `80px repeat(${days}, 1fr)`
    : `110px repeat(${days}, 1fr)`

  return (
    <>
      <section className="card">
        <div className="cal-header-row">
          <h3 className="cal-title">{days}-Day Adherence + Pain Map</h3>
          {pc.isMember ? (
            pc.noteSigned ? (
              <button
                type="button"
                className="cal-pdf-btn"
                onClick={() => pc.downloadNote('pdf')}
                disabled={pc.downloading}
              >
                {pc.downloading ? 'Preparing…' : 'Download note'}
              </button>
            ) : pc.canSend ? (
              <button
                type="button"
                className="cal-pdf-btn"
                onClick={pc.sendNote}
                disabled={pc.sendingNote}
              >
                {pc.sendingNote
                  ? 'Sending…'
                  : pc.noteStatus === 'needs_resubmit'
                    ? 'Resubmit note'
                    : 'Send note to clinician'}
              </button>
            ) : (
              <button type="button" className="cal-pdf-btn" onClick={pc.previewNote}>
                Preview note
              </button>
            )
          ) : (
            // Only offer paid sign-off when PelviaCare+ is live; when it's gated
            // off the homepage's ReportCard carries the "coming soon" waitlist.
            pc.available && loggedDays > 0 && (
              <button type="button" className="cal-pdf-btn" onClick={pc.startCheckout}>
                Get my note signed — $300
              </button>
            )
          )}
        </div>

        <div className="cal-grid-wrap">
          {/* Day number header */}
          <div className="cal-row" style={{ gridTemplateColumns: colTemplate }}>
            <div />
            {dayKeys.map((k) => (
              <div key={k} className="cal-hcell">
                {new Date(k + 'T12:00:00').getDate()}
              </div>
            ))}
          </div>

          {/* Cycle strip */}
          <div className="cal-row" style={{ gridTemplateColumns: colTemplate }}>
            <div className="cal-label">Cycle</div>
            {dayKeys.map((k) => {
              // Logged bleeding flow wins; otherwise tint by predicted phase.
              const logged = trackMap[k]?.cycle_phase
              const predictedPhase = phaseCalendar?.[k]
              const bg = logged
                ? CYCLE_COLORS[logged]
                : predictedPhase
                  ? PHASE_TINT[predictedPhase]
                  : undefined
              return <div key={k} className="cal-cycle-cell" style={{ background: bg }} />
            })}
          </div>

          {/* Med rows — click a cell to toggle taken/not-taken */}
          {meds.map((med) => (
            <div
              key={med.id}
              className="cal-row"
              style={{ gridTemplateColumns: colTemplate }}
            >
              <div className="cal-label" title={med.name}>
                <span className="cal-dot" style={{ background: med.color }} />
                <span className="cal-label-text">{med.name.split(' ')[0]}</span>
              </div>
              {dayKeys.map((k) => {
                const taken = !!adhMap[k]?.[med.id]
                return (
                  <div
                    key={k}
                    className={`cal-cell ${taken ? 'taken' : 'missed'} ${activeDay === k ? 'selected' : ''}`}
                    style={taken ? { background: med.color } : undefined}
                    title={`${taken ? 'Taken' : 'Not taken'} — ${med.name.split(' ')[0]}, ${formatDay(k)}`}
                    onClick={() => toggleMed(med.id, k, taken)}
                  />
                )
              })}
            </div>
          ))}

          {/* Pain row — click a cell to open the inline 0–10 picker */}
          <div className="cal-row" style={{ gridTemplateColumns: colTemplate }}>
            <div className="cal-label">
              <span className="cal-dot" style={{ background: 'var(--danger)' }} />
              Pain
            </div>
            {dayKeys.map((k) => {
              const pain = painMap[k]
              const logged = pain?.pain_score != null && !pain.zero_filled
              return (
                <div
                  key={k}
                  className={`cal-pain-cell ${activeDay === k ? 'selected' : ''}`}
                  style={
                    logged
                      ? { background: painColor(pain!.pain_score!), color: painTextColor(pain!.pain_score!) }
                      : { background: 'var(--surface2)', color: 'var(--text-muted)' }
                  }
                  title={`Pain — ${formatDay(k)}`}
                  onClick={(e) => {
                    // Read the rect synchronously in the handler: React nulls
                    // out e.currentTarget once the handler returns, and the
                    // setState updater below may run in the render phase (when
                    // the fiber has pending lanes), so reading currentTarget
                    // inside the updater crashes intermittently.
                    const rect = e.currentTarget.getBoundingClientRect()
                    setPainPicker((prev) => (prev?.k === k ? null : { k, rect }))
                  }}
                >
                  {logged ? pain!.pain_score : '·'}
                </div>
              )
            })}
          </div>

          {/* Notes row — click a day's icon to open its note card */}
          <div className="cal-row" style={{ gridTemplateColumns: colTemplate }}>
            <div className="cal-label">
              <span className="cal-dot" style={{ background: 'var(--text-muted)' }} />
              Notes
            </div>
            {dayKeys.map((k) => {
              const hasNote = Boolean(trackMap[k]?.notes) || Boolean(painMap[k]?.notes)
              return (
                <button
                  key={k}
                  type="button"
                  className={`cal-note-cell ${hasNote ? 'has-note' : ''} ${activeDay === k ? 'selected' : ''}`}
                  title={`${hasNote ? 'Edit note' : 'Add note'} — ${formatDay(k)}`}
                  onClick={() => setNoteCardFor(k)}
                >
                  ✎
                </button>
              )
            })}
          </div>
        </div>

        <div className="cal-legend">
          {meds.map((m) => (
            <div key={m.id} className="cal-legend-item">
              <span className="cal-legend-sq" style={{ background: m.color }} />
              {m.name.split(' ')[0]}
            </div>
          ))}
          <div className="cal-legend-item">
            <span className="cal-legend-sq" style={{ background: painColor(9) }} />
            Pain (high)
          </div>
          <div className="cal-legend-item">
            <span className="cal-legend-sq" style={{ background: painColor(1) }} />
            Pain (low)
          </div>
        </div>

        {adhStats.length > 0 && (
          <div className="cal-adherence">
            <div className="cal-adherence-title">Adherence</div>
            {adhStats.map((s) => {
              const isPrn = s.frequency === 'as needed'
              const pct = isPrn ? Math.min(((s.uses || 0) / 30) * 100, 100) : s.pct
              const label = isPrn ? `${s.uses || 0} uses` : `${s.pct}%`
              return (
                <div key={s.id} className="adh-row">
                  <div className="adh-label">{s.name}</div>
                  <div className="adh-track">
                    <div className="adh-fill" style={{ width: `${pct}%`, background: s.color }} />
                  </div>
                  <div className="adh-pct">{label}</div>
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* Inline pain picker, anchored to the clicked cell */}
      {painPicker && (
        <>
          <div className="cal-pop-backdrop" onClick={() => setPainPicker(null)} />
          <div className="cal-pain-pop" style={pickerStyle(painPicker.rect)}>
            {Array.from({ length: 11 }, (_, i) => (
              <button
                key={i}
                type="button"
                className="cal-pain-pop-btn"
                style={{ background: painColor(i), color: painTextColor(i) }}
                onClick={() => logPain(painPicker.k, i)}
              >
                {i}
              </button>
            ))}
          </div>
        </>
      )}

      {noteCardFor && (
        <CalendarDayDetail
          // Remount on day change so the notes textarea re-seeds from the new
          // day's saved note. Without a changing key React reuses the instance
          // and the useState initializer keeps the previous day's text.
          key={noteCardFor}
          dateKey={noteCardFor}
          today={today}
          pain={painMap[noteCardFor]}
          tracking={trackMap[noteCardFor]}
          onClose={() => setNoteCardFor(null)}
          onChange={refreshCalendar}
        />
      )}

      {pc.modals}
    </>
  )
}

interface DayDetailProps {
  dateKey: string
  today: string
  pain: CalendarPainDay | undefined
  tracking: CalendarTrackingDay | undefined
  onClose: () => void
  onChange: () => void
}

// Bottom card: read-only context for a day plus a free-text note editor.
// Med adherence and pain scores are edited directly in the grid above, so this
// card is now purely for typing a note about the day.
function CalendarDayDetail({
  dateKey,
  today,
  pain,
  tracking,
  onClose,
  onChange,
}: DayDetailProps) {
  const [notes, setNotes] = useState(tracking?.notes || pain?.notes || '')
  const [savingNotes, setSavingNotes] = useState(false)
  const isToday = dateKey === today
  const label = formatDay(dateKey)

  const hasLoggedPain = pain?.pain_score != null && !pain.zero_filled
  const entries = pain?.entries || []
  const multiEntry = isToday && entries.length > 1

  const saveNotes = async () => {
    setSavingNotes(true)
    try {
      await api.post('/api/patient/day-note', { log_date: dateKey, notes })
    } finally {
      setSavingNotes(false)
    }
    onChange()
  }

  return (
    <section className="card cal-day-detail">
      <div className="cal-day-head">
        <div>
          <strong className="cal-day-title">{label}</strong>
          {tracking?.cycle_phase && (
            <div className="cal-day-phase">{tracking.cycle_phase.replace(/_/g, ' ')}</div>
          )}
        </div>
        {hasLoggedPain && !multiEntry && (
          <div
            className="cal-day-score"
            style={{
              background: painColor(pain!.pain_score!),
              color: painTextColor(pain!.pain_score!),
            }}
          >
            {pain!.pain_score}/10
          </div>
        )}
        {multiEntry && <div className="cal-day-score-soft">{entries.length} entries</div>}
        <button
          type="button"
          className="cal-day-close"
          onClick={onClose}
          aria-label="Close day detail"
        >
          ×
        </button>
      </div>

      {isToday && entries.length > 0 && (
        <div className="cal-entries">
          {entries.map((e, idx) => (
            <div key={e.id ?? idx} className="cal-entry-row">
              <div
                className="cal-entry-score"
                style={{
                  background: painColor(e.pain_score),
                  color: painTextColor(e.pain_score),
                }}
              >
                {e.pain_score}
              </div>
              <div className="cal-entry-body">
                <div className="cal-entry-line">
                  {e.pain_score}/10
                  {e.created_at && (
                    <span className="cal-entry-time"> at {e.created_at}</span>
                  )}
                </div>
                {e.pain_character && e.pain_character.length > 0 && (
                  <div className="cal-entry-meta">{e.pain_character.join(', ')}</div>
                )}
                {e.notes && <div className="cal-entry-meta cal-entry-notes">{e.notes}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tracking?.symptoms && tracking.symptoms.length > 0 && (
        <div className="cal-detail-meta">Symptoms: {tracking.symptoms.join(', ')}</div>
      )}
      {tracking?.sleep_hours != null && (
        <div className="cal-detail-meta">Sleep: {tracking.sleep_hours}h</div>
      )}
      {tracking?.food_tags && tracking.food_tags.length > 0 && (
        <div className="cal-detail-meta">
          Food: {tracking.food_tags.map((t) => t.replace(/_/g, ' ')).join(', ')}
        </div>
      )}

      <div className="cal-detail-section">
        <div className="cal-detail-label">Note for this day</div>
        <textarea
          className="cal-detail-notes"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Anything notable about this day — symptoms, triggers, how you felt…"
        />
        <button
          type="button"
          className="cal-notes-save"
          onClick={saveNotes}
          disabled={savingNotes}
        >
          {savingNotes ? 'Saving…' : 'Save note'}
        </button>
      </div>
    </section>
  )
}
